import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { formatDate } from '@/lib/utils';
import { useSensorStream } from '../hooks/useSensorEdit';
import { SensorStreamsCombobox } from './SensorStreamsCombobox';
import type { SensorEditData, SensorEditOptions } from '../types';

interface SensorInfoCardProps {
  sensor: SensorEditData;
  options: SensorEditOptions;
  stream: string;
  isAdmin: boolean;
  onChange: (field: string, value: string) => void;
}

export function SensorInfoCard({
  sensor,
  options,
  stream,
  isAdmin,
  onChange,
}: SensorInfoCardProps) {
  const selectedStreamFromOptions = useMemo(
    () => options.streams.find((s) => s._id === stream),
    [stream, options.streams],
  );
  const selectedStreamLookupId = stream && !selectedStreamFromOptions ? stream : undefined;
  const selectedStreamQuery = useSensorStream(selectedStreamLookupId);
  const selectedStream = selectedStreamFromOptions ?? selectedStreamQuery.data ?? null;

  const transmitterName = useMemo(() => {
    if (!stream) return '\u2014';
    if (!selectedStream?.transmitter) return '\u2014';
    const transmitter = options.transmitters.find(
      (t) => t._id === selectedStream.transmitter,
    );
    return transmitter?.dev_id || transmitter?.name || selectedStream.transmitterName || '\u2014';
  }, [stream, selectedStream, options.transmitters]);

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Info
      </h4>
      <div className="rounded-md bg-muted/50 p-3 space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">ID</span>
          <span className="font-mono text-xs">{sensor._id}</span>
        </div>
        {isAdmin && (
          <div>
            <Label className="text-xs">Stream</Label>
            <SensorStreamsCombobox
              value={stream || 'all'}
              organisationId={sensor.organisation}
              currentSensorId={sensor._id}
              className="h-8"
              onChange={(value) => onChange('stream', value === 'all' ? '' : value)}
            />
          </div>
        )}
        {!isAdmin && sensor.stream_name && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Stream</span>
            <span>{sensor.stream_name}</span>
          </div>
        )}
        <div className="flex justify-between">
          <span className="text-muted-foreground">Transmitter</span>
          <span>{transmitterName}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Installed</span>
          <span>{sensor.first_data_at ? formatDate(sensor.first_data_at) : '\u2014'}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Last Updated</span>
          <span>{sensor.timestamp ? formatDate(sensor.timestamp) : '\u2014'}</span>
        </div>
      </div>
    </div>
  );
}
